import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Palette as PaletteIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/ui/DataTable';
import { LoadingPage } from '../../components/ui/LoadingSpinner';
import apiClient from '../../lib/api';
import { normalizeListPagination } from '../../lib/normalizeListPagination';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const CardsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentAccountType } = useAuth();
  
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCards, setSelectedCards] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [cardStats, setCardStats] = useState({
    publishedCards: null,
    draftCards: null,
  });

  const fetchCards = async (searchParams) => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!user) {
        console.warn('No user found, cannot load cards');
        return;
      }
      
      const params = {
        page: searchParams?.page ?? pagination.page,
        limit: searchParams?.rowsPerPage ?? pagination.limit,
        search: searchParams?.searchTerm || searchTerm,
      };
      
      // Check URL query params for designerId (allows manual filtering)
      const urlParams = new URLSearchParams(location.search);
      const designerIdFromUrl = urlParams.get('designerId');
      
      // Apply context-based filtering
      if (currentAccountType === 'designer' && user.id) {
        // Designers only see their own cards
        params.designerId = user.id;
      } else if (designerIdFromUrl) {
        // Allow manual filtering by designerId from URL (for admins)
        params.designerId = designerIdFromUrl;
      }
      // Admins see all cards (unless designerId is in URL)
      
      // Add filter parameters
      if (searchParams?.filterValues) {
        const filters = searchParams.filterValues;
        if (filters.status && filters.status !== 'all') {
          params.status = filters.status;
        }
        if (filters.category && filters.category !== 'all') {
          params.category = filters.category;
        }
      }
      
      console.log('Loading cards with params:', params);
      
      const response = await apiClient.getCards(params);
      
      console.log('API Response:', response);
      console.log('Cards data:', response.data);
      setCards(response.data || []);
      setPagination(normalizeListPagination(response, { limit: 10 }));
    } catch (err) {
      console.error('Error fetching cards:', err);
      setError('Failed to load cards. Please try again.');
      toast.error('Failed to load cards. Please try again.');
      setCards([]);
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch card stats when filtered by designerId
  const fetchCardStats = async () => {
    try {
      const urlParams = new URLSearchParams(location.search);
      const designerIdFromUrl = urlParams.get('designerId');
      
      let designerId = null;
      if (currentAccountType === 'designer' && user?.id) {
        designerId = user.id;
      } else if (designerIdFromUrl) {
        designerId = designerIdFromUrl;
      }
      
      // Only fetch stats if we have a designerId filter
      if (designerId) {
        const response = await apiClient.getCardStats(designerId);
        if (response.success && response.data) {
          const cardsByStatus = response.data.cardsByStatus || {};
          setCardStats({
            publishedCards: cardsByStatus.published || 0,
            draftCards: cardsByStatus.draft || 0,
          });
        }
      } else {
        // Reset stats when no designer filter
        setCardStats({
          publishedCards: null,
          draftCards: null,
        });
      }
    } catch (error) {
      console.error('Error fetching card stats:', error);
      // Don't show error, just use null to fall back to counting from cards array
      setCardStats({
        publishedCards: null,
        draftCards: null,
      });
    }
  };

  useEffect(() => {
    if (user) {
      fetchCards();
      fetchCardStats();
    }
  }, [user, currentAccountType, location.search]); // Re-fetch when URL query params change

  // Debug: Log cards state changes
  useEffect(() => {
    console.log('Cards state updated:', cards);
  }, [cards]);

  // Conditionally include designer column only if not logged in as designer
  const isDesigner = currentAccountType === 'designer' || user?.type === 'designer';
  
  const columns = [
    { 
      id: 'title', 
      label: 'Title', 
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {value || 'Untitled Card'}
        </Typography>
      )
    },
    // Only show designer column if not logged in as designer
    ...(!isDesigner ? [{
      id: 'designer', 
      label: 'Designer', 
      sortable: true,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
            {row.designer ? `${row.designer.firstName?.[0] || ''}${row.designer.lastName?.[0] || ''}` : '?'}
          </Avatar>
          <Typography variant="body2">
            {row.designer ? `${row.designer.firstName || ''} ${row.designer.lastName || ''}` : 'Unknown Designer'}
          </Typography>
        </Box>
      )
    }] : []),
    { 
      id: 'status', 
      label: 'Status', 
      sortable: true,
      render: (value, row) => {
        const statusColors = {
          'draft': 'warning',
          'published': 'success',
          'archived': 'default'
        };
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              label={value?.charAt(0).toUpperCase() + value?.slice(1) || 'Draft'}
              size="small"
              color={statusColors[value] || 'default'}
            />
            {row.aiReferenceBlocked ? (
              <Chip
                label="AI Protected"
                size="small"
                variant="outlined"
              />
            ) : null}
          </Box>
        );
      }
    },
    {
      id: 'tags',
      label: 'Tags', 
      sortable: false,
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {row.tags && row.tags.length > 0 ? (
            row.tags.slice(0, 2).map((tag, index) => (
              <Chip key={index} label={tag} size="small" variant="outlined" />
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              No tags
            </Typography>
          )}
          {row.tags && row.tags.length > 2 && (
            <Typography variant="caption" color="text.secondary">
              +{row.tags.length - 2} more
            </Typography>
          )}
        </Box>
      )
    },
    { 
      id: 'sold', 
      label: 'Sold', 
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {row.sold || 0}
        </Typography>
      )
    },
    { 
      id: 'revenue', 
      label: 'Revenue', 
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
          ${(row.revenue || 0).toFixed(2)}
        </Typography>
      )
    },
  ];

  const filters = [
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search by title, tags, or designer name...'
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'published', label: 'Published' },
        { value: 'archived', label: 'Archived' },
      ],
    },
  ];

  const handleRowSelect = (row) => {
    navigate(`/cards/${row.id}`);
  };

  const handleSearch = (searchParams) => {
    console.log('Search params:', searchParams);
    fetchCards(searchParams);
  };

  const handleAddCard = () => {
    navigate('/cards/add');
  };

  // Remove the full-page loading state that causes unmounting
  // Instead, show loading state within the DataTable component

  return (
    <DashboardLayout title="Cards">
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Cards
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and view all cards in the system
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
          gap: 2 
        }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PaletteIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Cards
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {pagination.total}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Published Cards
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {cardStats.publishedCards !== null 
                  ? cardStats.publishedCards 
                  : cards.filter(card => card.status === 'published').length}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Draft Cards
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {cardStats.draftCards !== null 
                  ? cardStats.draftCards 
                  : cards.filter(card => card.status === 'draft').length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Cards Table */}
      <Box sx={{ mt: 4 }}>         
        <DataTable
          data={cards}
          title="Cards"
          onAdd={handleAddCard}
          addButtonText="Add Card"
          columns={columns}
          filters={filters}
          onSelect={handleRowSelect}
          onSearch={handleSearch}
          totalPages={pagination.totalPages}
          currentPage={pagination.page}
          loading={isLoading}
          onRowClick={(row) => navigate(`/cards/${row.id}`)}
        />
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default CardsPage;
